<template>
  <el-dialog title="新建流程" :visible.sync="visible" width="720px" :close-on-click-modal="false" append-to-body
    @closed="onClosed">
    <el-form ref="form" :model="form" :rules="rules" label-width="100px" size="small">
      <el-form-item label="问题标题" prop="questionTitle">
        <el-input v-model="form.questionTitle" maxlength="255" show-word-limit />
      </el-form-item>
      <el-form-item label="问题描述" prop="questionDescription">
        <el-input v-model="form.questionDescription" type="textarea" :rows="4" />
      </el-form-item>
      <el-form-item label="流程标签" prop="tag">
        <el-select v-model="form.tag" placeholder="请选择标签" style="width: 100%">
          <el-option v-for="(tag, idx) in workflowTagOptions" :key="idx" :label="tag" :value="tag" />
        </el-select>
      </el-form-item>
      <el-form-item label="附件">
        <multiple-file-upload v-model="form.questionAttachments" file-path="/workflow/question" :limit="10" />
      </el-form-item>
      <el-form-item label="业务附件">
        <AttachmentSelector v-model="form.bizAttachments" multiple />
      </el-form-item>
      <el-row :gutter="12" class="related-party-form-row">
        <el-col :span="12">
          <el-form-item label="关联方角色" prop="relatedPartyRole">
            <el-select v-model="form.relatedPartyRole" clearable filterable placeholder="请选择角色" style="width: 100%"
              @change="onRelatedPartyRoleChange">
              <el-option v-for="(opt, idx) in relatedPartyRoleOpts" :key="idx" :label="opt.label" :value="opt.value" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="关联方" prop="relatedPartyId">
            <el-select v-model="form.relatedPartyId" filterable clearable placeholder="请选择关联方" style="width: 100%"
              :disabled="!form.relatedPartyRole" v-loading="relatedPartySearchLoading">
              <el-option v-for="item in relatedPartyOptions" :key="item.relatedPartyId" :label="item.relatedPartyName"
                :value="item.relatedPartyId" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item label="处理人" prop="firstAssigneeId">
        <el-select v-model="form.firstAssigneeId" filterable placeholder="请选择后台用户" style="width: 100%"
          @change="onProcessAssigneeChange">
          <el-option v-for="u in adminOptions" :key="u.id" :label="formatAdminLabel(u)" :value="u.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="截止时间" prop="deadline">
        <el-date-picker v-model="form.deadline" type="datetime" placeholder="选择截止时间" value-format="yyyy-MM-dd HH:mm:ss"
          format="yyyy-MM-dd HH:mm" style="width: 100%" />
      </el-form-item>
    </el-form>
    <span slot="footer">
      <el-button size="small" @click="visible = false">取 消</el-button>
      <el-button type="primary" size="small" :loading="submitting" @click="submit">提 交</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapState } from "vuex";
import { getAdminList } from "@/api/admin";
import { createWorkflowProcess, getWorkflowRelatedParties } from "@/api/workflow";
import { DateUtil } from "@/util";
import AttachmentSelector from "@/components/AttachmentSelector";

/** 与流程页一致：选项文案即提交与存储值 */
const WORKFLOW_TAG_OPTIONS = ["安全类", "服务类", "程序类", "运营类", "客服质检", "其他"];

function defaultWorkflowDeadline() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return DateUtil.format(d, "yyyy-MM-dd hh:mm:ss");
}

export default {
  name: "WorkflowCreateModal",
  components: { AttachmentSelector },
  data() {
    return {
      visible: false,
      submitting: false,
      onSuccessCallback: null,
      adminOptions: [],
      relatedPartyOptions: [],
      relatedPartySearchLoading: false,
      workflowTagOptions: WORKFLOW_TAG_OPTIONS,
      form: {
        questionTitle: "",
        questionDescription: "",
        tag: "",
        questionAttachments: [],
        bizAttachments: [],
        firstAssigneeId: null,
        firstAssigneeName: "",
        deadline: defaultWorkflowDeadline(),
        relatedPartyRole: null,
        relatedPartyId: null,
        relatedPartyName: null
      }
    };
  },
  computed: {
    ...mapState({
      relatedPartyRoleOpts: (state) => state.enumMap["WORKFLOW_RELATED_PARTY_ROLE"] || []
    }),
    rules() {
      return {
        questionTitle: [{ required: true, message: "请输入标题", trigger: "blur" }],
        tag: [{ required: true, message: "请选择流程标签", trigger: "change" }],
        firstAssigneeId: [{ required: true, message: "请选择处理人", trigger: "change" }]
      };
    }
  },
  created() {
    this.loadAdminList();
  },
  methods: {
    /**
     * 打开新建流程弹窗（由 Index 事件总线转发）
     * @param {Object} [opts]
     * @param {Function} [opts.onSuccess] 创建成功后的回调
     * @param {Function} [opts.callback] 同 onSuccess（与 PunishModal 等全局弹窗一致）
     * @param {Object} [opts.initialForm] 预填表单（会与默认字段合并）
     */
    show(opts) {
      const { onSuccess, callback, initialForm } = opts || {};
      const cb = typeof onSuccess === "function" ? onSuccess : typeof callback === "function" ? callback : null;
      this.onSuccessCallback = cb;
      this.resetForm(initialForm);
      this.visible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate());
    },

    emptyForm() {
      return {
        questionTitle: "",
        questionDescription: "",
        tag: "",
        questionAttachments: [],
        bizAttachments: [],
        firstAssigneeId: null,
        firstAssigneeName: "",
        deadline: defaultWorkflowDeadline(),
        relatedPartyRole: null,
        relatedPartyId: null,
        relatedPartyName: null
      };
    },

    resetForm(initialForm) {
      const base = this.emptyForm();
      if (initialForm && typeof initialForm === "object") {
        Object.assign(base, initialForm);
      }
      this.form = base;
      this.relatedPartyOptions = [];
      if (this.form.relatedPartyRole) {
        this.loadRelatedPartiesForRole("");
      }
    },

    onClosed() {
      this.onSuccessCallback = null;
    },

    formatAdminLabel(u) {
      return `${u.name || ""}`;
    },

    loadAdminList() {
      const param = {
        page: { size: 500 },
        param: { normalUser: true }
      };
      getAdminList(param).then((res) => {
        this.adminOptions = res.data.records || [];
      });
    },

    onProcessAssigneeChange(id) {
      const u = this.adminOptions.find((x) => x.id === id);
      this.form.firstAssigneeName = u ? u.name || u.username : "";
    },

    onRelatedPartyRoleChange() {
      this.$set(this.form, "relatedPartyId", null);
      this.$set(this.form, "relatedPartyName", null);
      this.relatedPartyOptions = [];
      this.loadRelatedPartiesForRole("");
    },

    loadRelatedPartiesForRole(key) {
      if (!this.form.relatedPartyRole) {
        this.relatedPartyOptions = [];
        return;
      }
      this.relatedPartySearchLoading = true;
      getWorkflowRelatedParties({
        role: this.form.relatedPartyRole,
        key: key || "",
        _hideLoading: true
      })
        .then((res) => {
          this.relatedPartyOptions = res.data || [];
        })
        .finally(() => {
          this.relatedPartySearchLoading = false;
        });
    },

    submit() {
      this.$refs.form.validate((ok) => {
        if (!ok) return;
        const body = {
          questionTitle: this.form.questionTitle,
          questionDescription: this.form.questionDescription,
          tag: this.form.tag,
          questionAttachments:
            this.form.questionAttachments && this.form.questionAttachments.length
              ? this.form.questionAttachments
              : null,
          bizAttachments:
            this.form.bizAttachments && this.form.bizAttachments.length ? this.form.bizAttachments : null,
          firstAssigneeId: this.form.firstAssigneeId,
          firstAssigneeName: this.form.firstAssigneeName || null,
          deadline: this.form.deadline || null,
          relatedPartyRole: this.form.relatedPartyRole,
          relatedPartyId: this.form.relatedPartyId || null
        };
        if (body.relatedPartyId) {
          const item = this.relatedPartyOptions.find(
            (x) => String(x.relatedPartyId) === String(this.form.relatedPartyId)
          );
          body.relatedPartyName = item && item.relatedPartyName ? item.relatedPartyName : null;
        }
        this.submitting = true;
        createWorkflowProcess(body)
          .then(() => {
            this.$message.success("创建成功");
            this.visible = false;
            const cb = this.onSuccessCallback;
            this.onSuccessCallback = null;
            cb && cb();
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    }
  }
};
</script>
